import { useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";
import { fmtDate } from "../lib/date";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Plus, FileText, X, MagnifyingGlass, Trash, ArrowClockwise, DownloadSimple } from "@phosphor-icons/react";

function exportCsv(policies) {
  const cols = ["Policy Type", "Policy Name", "Insurer", "Broker", "Policy Number", "Insured Entity", "Start Date", "Expiry Date", "Days to Expiry", "Status", "Cover Limit", "Excess", "Renewal Status", "Covered Assets", "Site Count", "Project Count", "Replaces", "Replaced By", "Notes"];
  const rows = policies.map((p) => [p.policy_type_name, p.name || p.policy_ref, p.provider_name, p.broker, p.policy_number, p.insured_entity, p.effective_from, p.effective_until, p.days_to_expiry, p.state, p.cover_limit, p.excess, p.renewal_status, p.covered_asset_count, p.site_count, p.project_count, p.replaces_policy_name, p.replaced_by_policy_name, p.notes]);
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [cols, ...rows].map((r) => r.map(esc).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url; a.download = "insurance-register.csv"; a.click(); URL.revokeObjectURL(url);
}

const STATE_BADGE = {
  active: "bg-emerald-600 text-white", expiring: "bg-amber-500 text-slate-900",
  expired: "bg-red-600 text-white", cancelled: "bg-slate-500 text-white", replaced: "bg-slate-400 text-white",
};
const RENEWAL_OPTIONS = [
  ["not_started", "Not started"], ["requested", "Requested"], ["quoted", "Quoted"],
  ["awaiting_documents", "Awaiting documents"], ["renewed", "Renewed"], ["not_renewing", "Not renewing"],
];
const RENEWAL_LABEL = Object.fromEntries(RENEWAL_OPTIONS);
const DOC_CATEGORIES = [["certificate", "Certificate"], ["schedule", "Policy Schedule"], ["endorsement", "Endorsement"], ["wording", "Policy Wording"], ["renewal", "Renewal Document"], ["other", "Other"]];
const GROUPS = [["vehicle", "Vehicles"], ["plant", "Plant"], ["tool", "Tools"], ["other", "Other"]];

function Badge({ state }) {
  return <span data-testid="policy-state-badge" className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${STATE_BADGE[state] || "bg-slate-200 text-slate-700"}`}>{state}</span>;
}

export default function InsurancePage() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("asset_manager", "supervisor", "administrator");
  const [policies, setPolicies] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState("policies");
  const isAdmin = hasPerm("administrator");

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q); if (status) p.set("status", status);
    api.get(`/insurance/policies?${p.toString()}`).then((r) => setPolicies(r.data)).catch(() => {});
  }, [q, status]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  return (
    <div className="flex flex-col gap-5 max-w-5xl">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight text-slate-900 flex items-center gap-2"><ShieldCheck size={28} weight="bold" />Insurance</h1>
          <p className="font-body text-sm text-slate-500">Company insurance policies, cover & renewals.</p>
        </div>
        <div className="flex items-center gap-2">
          {view === "policies" && <Button data-testid="insurance-export" onClick={() => exportCsv(policies)} variant="outline" className="font-semibold"><DownloadSimple size={16} weight="bold" className="mr-1" />Export CSV</Button>}
          {canManage && view === "policies" && <Button data-testid="insurance-new" onClick={() => setCreating(true)} className="bg-blue-600 hover:bg-blue-700 font-bold"><Plus size={16} weight="bold" className="mr-1" />New Policy</Button>}
        </div>
      </div>

      <div className="flex gap-1" data-testid="insurance-view-tabs">
        <button data-testid="ins-tab-policies" onClick={() => setView("policies")} className={`text-sm font-semibold px-3 py-1.5 rounded ${view === "policies" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>Policies</button>
        <button data-testid="ins-tab-requirements" onClick={() => setView("requirements")} className={`text-sm font-semibold px-3 py-1.5 rounded ${view === "requirements" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>Asset-Type Requirements</button>
      </div>

      {view === "requirements" && <AssetTypeRequirements isAdmin={isAdmin} />}
      {view === "policies" && (<>

      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="insurance-search" placeholder="Search policies, insurer, number…" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 pl-9" />
        </div>
        <select data-testid="insurance-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 border-2 border-slate-300 rounded px-2 text-sm">
          <option value="">All statuses</option>
          <option value="active">Active</option><option value="expiring">Expiring</option><option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option><option value="replaced">Replaced</option>
        </select>
      </div>

      <div className="grid gap-2">
        {policies.length === 0 && <p data-testid="insurance-empty" className="text-slate-400 text-sm">No policies yet. {canManage ? "Create one to get started." : ""}</p>}
        {policies.map((p) => (
          <button key={p.id} data-testid={`policy-card-${p.id}`} onClick={() => setOpenId(p.id)}
            className="text-left bg-white border-2 border-slate-200 hover:border-blue-300 rounded-lg p-4 transition-colors">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="font-display font-black text-slate-900">{p.name || p.policy_ref}</div>
              <Badge state={p.state} />
            </div>
            <div className="font-mono text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
              {p.policy_type_name && <span>{p.policy_type_name}</span>}
              {p.provider_name && <span>· {p.provider_name}</span>}
              {p.policy_number && <span>· #{p.policy_number}</span>}
              <span>· expires {fmtDate(p.effective_until) || "—"}</span>
              <span>· {p.coverage_count} coverage · {p.document_count} doc</span>
              {p.renewal_status && p.renewal_status !== "not_started" && <span className="text-amber-700">· renewal: {RENEWAL_LABEL[p.renewal_status]}</span>}
            </div>
          </button>
        ))}
      </div>
      </>)}

      {creating && <PolicyDrawer mode="create" canManage={canManage} onClose={() => setCreating(false)} onSaved={(id) => { setCreating(false); load(); setOpenId(id); }} />}
      {openId && <PolicyDrawer mode="edit" policyId={openId} canManage={canManage} onClose={() => setOpenId(null)} onSaved={() => load()} />}
    </div>
  );
}

function PolicyDrawer({ mode, policyId, canManage, onClose, onSaved }) {
  const [policy, setPolicy] = useState(null);
  const [providers, setProviders] = useState([]);
  const [types, setTypes] = useState([]);
  const [f, setF] = useState({
    name: "", policy_type_id: "", provider_id: "", broker: "", policy_number: "", cover_limit: "", excess: "",
    insured_entity: "", contact_claims: "", effective_from: "", effective_until: "", notes: "", newInsurer: "", newType: "",
  });

  const load = useCallback(() => {
    if (mode === "edit") api.get(`/insurance/policies/${policyId}`).then((r) => {
      setPolicy(r.data);
      setF((prev) => ({ ...prev, ...pick(r.data) }));
    }).catch(() => {});
  }, [mode, policyId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/config/insurance-providers").then((r) => setProviders(r.data)).catch(() => {});
    api.get("/config/insurance-policy-types").then((r) => setTypes(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const resolveProvider = async () => {
    if (f.newInsurer && f.newInsurer.trim()) {
      const r = await api.post("/config/insurance-providers", { name: f.newInsurer.trim() });
      return r.data.id;
    }
    return f.provider_id || null;
  };
  const resolvePolicyType = async () => {
    if (f.newType && f.newType.trim()) {
      const r = await api.post("/config/insurance-policy-types", { name: f.newType.trim() });
      setTypes((t) => [...t, r.data]);
      return r.data.id;
    }
    return f.policy_type_id || null;
  };
  const save = async () => {
    if (!f.name && !f.policy_number) { toast.error("Enter a policy name or number"); return; }
    try {
      const provider_id = await resolveProvider();
      const policy_type_id = await resolvePolicyType();
      const body = {
        name: f.name, policy_type_id, provider_id, broker: f.broker, policy_number: f.policy_number,
        cover_limit: f.cover_limit, excess: f.excess, insured_entity: f.insured_entity, contact_claims: f.contact_claims,
        effective_from: f.effective_from || null, effective_until: f.effective_until || null, notes: f.notes,
      };
      if (mode === "create") { const r = await api.post("/insurance/policies", body); toast.success("Policy created"); onSaved(r.data.id); }
      else { await api.put(`/insurance/policies/${policyId}`, body); toast.success("Policy saved"); load(); onSaved(); }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const disabled = !canManage;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" data-testid="policy-drawer" onClick={onClose}>
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl font-black">{mode === "create" ? "New Policy" : (policy?.name || policy?.policy_ref || "Policy")}</h3>
          <button data-testid="policy-close" onClick={onClose}><X size={22} /></button>
        </div>
        {mode === "edit" && policy && (
          <div className="flex items-center gap-2 mb-3"><Badge state={policy.state} />
            {policy.replaces_policy && <span className="text-xs text-slate-500">replaces {policy.replaces_policy.name}</span>}
            {policy.replaced_by_policy && <span className="text-xs text-slate-500">replaced by {policy.replaced_by_policy.name}</span>}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <Field label="Policy name" className="col-span-2"><Input data-testid="policy-name" disabled={disabled} value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <Field label="Policy type"><select data-testid="policy-type" disabled={disabled} className="h-10 w-full border-2 border-slate-300 rounded px-2 text-sm" value={f.policy_type_id} onChange={(e) => set("policy_type_id", e.target.value)}>
            <option value="">Select…</option>{types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></Field>
          <Field label="…or new type"><Input data-testid="policy-new-type" disabled={disabled} placeholder="e.g. Combined PL & EL" value={f.newType} onChange={(e) => set("newType", e.target.value)} /></Field>
          <Field label="Policy number"><Input data-testid="policy-number" disabled={disabled} value={f.policy_number} onChange={(e) => set("policy_number", e.target.value)} /></Field>
          <Field label="Insurer"><select data-testid="policy-provider" disabled={disabled} className="h-10 w-full border-2 border-slate-300 rounded px-2 text-sm" value={f.provider_id} onChange={(e) => set("provider_id", e.target.value)}>
            <option value="">Select insurer…</option>{providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
          <Field label="…or new insurer"><Input data-testid="policy-new-insurer" disabled={disabled} placeholder="Type a new insurer" value={f.newInsurer} onChange={(e) => set("newInsurer", e.target.value)} /></Field>
          <Field label="Broker"><Input data-testid="policy-broker" disabled={disabled} value={f.broker} onChange={(e) => set("broker", e.target.value)} /></Field>
          <Field label="Insured company / entity"><Input data-testid="policy-insured" disabled={disabled} value={f.insured_entity} onChange={(e) => set("insured_entity", e.target.value)} /></Field>
          <Field label="Start date"><Input data-testid="policy-start" type="date" disabled={disabled} value={f.effective_from || ""} onChange={(e) => set("effective_from", e.target.value)} /></Field>
          <Field label="Expiry / renewal date"><Input data-testid="policy-expiry" type="date" disabled={disabled} value={f.effective_until || ""} onChange={(e) => set("effective_until", e.target.value)} /></Field>
          <Field label="Cover limit"><Input data-testid="policy-cover" disabled={disabled} placeholder="e.g. £5,000,000" value={f.cover_limit} onChange={(e) => set("cover_limit", e.target.value)} /></Field>
          <Field label="Excess"><Input data-testid="policy-excess" disabled={disabled} placeholder="e.g. £500" value={f.excess} onChange={(e) => set("excess", e.target.value)} /></Field>
          <Field label="Contact / claims details" className="col-span-2"><Textarea data-testid="policy-contact" disabled={disabled} value={f.contact_claims} onChange={(e) => set("contact_claims", e.target.value)} /></Field>
          <Field label="Notes" className="col-span-2"><Textarea data-testid="policy-notes" disabled={disabled} value={f.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
        </div>
        {canManage && <Button data-testid="policy-save" onClick={save} className="h-10 w-full mt-3 bg-blue-600 hover:bg-blue-700 font-semibold">{mode === "create" ? "Create policy" : "Save policy"}</Button>}

        {mode === "edit" && policy && (
          <>
            <RenewalSection policy={policy} canManage={canManage} onChanged={() => { load(); onSaved(); }} />
            <CoverageSection policyId={policyId} canManage={canManage} />
            <DocumentsSection policy={policy} canManage={canManage} onChanged={load} />
            {canManage && <LifecycleSection policy={policy} onChanged={() => { load(); onSaved(); }} onClose={onClose} />}
          </>
        )}
      </div>
    </div>
  );
}

function pick(p) {
  return {
    name: p.name || "", policy_type_id: p.policy_type_id || "", provider_id: p.provider_id || "", broker: p.broker || "",
    policy_number: p.policy_number || "", cover_limit: p.cover_limit || "", excess: p.excess || "", insured_entity: p.insured_entity || "",
    contact_claims: p.contact_claims || "", effective_from: p.effective_from || "", effective_until: p.effective_until || "", notes: p.notes || "", newInsurer: "", newType: "",
  };
}
function Field({ label, className = "", children }) {
  return <div className={className}><label className="text-[11px] font-semibold text-slate-600">{label}</label>{children}</div>;
}

function RenewalSection({ policy, canManage, onChanged }) {
  const [rs, setRs] = useState(policy.renewal_status || "not_started");
  const [notes, setNotes] = useState(policy.renewal_notes || "");
  const save = async () => {
    try { await api.put(`/insurance/policies/${policy.id}/renewal`, { renewal_status: rs, renewal_notes: notes }); toast.success("Renewal updated"); onChanged(); }
    catch (e) { toast.error("Failed"); }
  };
  return (
    <div className="mt-5 border-t border-slate-200 pt-4" data-testid="renewal-section">
      <div className="font-display font-black text-slate-800 mb-2">Renewal</div>
      <select data-testid="renewal-status" disabled={!canManage} className="h-10 w-full border-2 border-slate-300 rounded px-2 text-sm" value={rs} onChange={(e) => setRs(e.target.value)}>
        {RENEWAL_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      {canManage && <><Textarea data-testid="renewal-notes" className="mt-2" placeholder="Renewal notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        <Button data-testid="renewal-save" onClick={save} className="h-9 mt-2 bg-slate-800 hover:bg-slate-700 font-semibold text-sm">Save renewal</Button></>}
    </div>
  );
}

function CoverageSection({ policyId, canManage }) {
  const [items, setItems] = useState([]);
  const [method, setMethod] = useState("specific");
  const [assets, setAssets] = useState([]);
  const [types, setTypes] = useState([]);
  const [preview, setPreview] = useState(null);
  const [assetQ, setAssetQ] = useState("");
  const [excludeMode, setExcludeMode] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(() => {
    api.get(`/insurance/policies/${policyId}/coverage`)
      .then((r) => setItems(r.data.coverage.map((c) => ({ scope: c.scope, scope_ref: c.scope_ref, mode: c.mode }))))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [policyId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/assets").then((r) => setAssets(r.data)).catch(() => {});
    api.get("/config/asset-types").then((r) => setTypes(r.data)).catch(() => {});
  }, []);

  const assetLabel = (id) => { const a = assets.find((x) => x.id === id); return a ? `${a.asset_number} — ${a.name}${a.registration ? ` (${a.registration})` : ""}` : id; };
  const typeLabel = (id) => types.find((t) => t.id === id)?.name || id;
  const labelFor = (it) => {
    if (it.scope === "asset") return assetLabel(it.scope_ref);
    if (it.scope === "asset_type") return `Type: ${typeLabel(it.scope_ref)}`;
    if (it.scope === "asset_group") return `Group: ${(GROUPS.find((g) => g[0] === it.scope_ref) || [])[1] || it.scope_ref}`;
    if (it.scope === "whole_company") return "Whole company";
    return `${it.scope}: ${it.scope_ref || ""}`;
  };

  const addItem = (scope, scope_ref, mode = "include") => {
    if (items.some((i) => i.scope === scope && i.scope_ref === scope_ref && i.mode === mode)) return;
    setItems((p) => [...p, { scope, scope_ref, mode }]);
  };
  const remove = (idx) => setItems((p) => p.filter((_, i) => i !== idx));

  const runPreview = async (list = items) => {
    try { const r = await api.post("/insurance/coverage-preview", { items: list }); setPreview(r.data); }
    catch (e) { toast.error("Preview failed"); }
  };
  const saveCoverage = async () => {
    try { const r = await api.put(`/insurance/policies/${policyId}/coverage`, { method, items }); toast.success(`Coverage saved — ${r.data.resolved_count} assets`); runPreview(); }
    catch (e) { toast.error("Failed"); }
  };

  const filteredAssets = assetQ ? assets.filter((a) => `${a.asset_number} ${a.name} ${a.registration || ""}`.toLowerCase().includes(assetQ.toLowerCase())) : assets.slice(0, 12);

  return (
    <div className="mt-5 border-t border-slate-200 pt-4" data-testid="coverage-section">
      <div className="font-display font-black text-slate-800 mb-2">Coverage</div>
      {!canManage && items.length === 0 && <p className="text-sm text-slate-400">No coverage set.</p>}

      {canManage && !loaded && <p className="text-xs text-slate-400" data-testid="coverage-loading">Loading coverage…</p>}
      {canManage && loaded && (
        <div className="flex flex-col gap-2">
          <label className="text-[11px] font-semibold text-slate-600">Coverage method</label>
          <div className="flex flex-wrap gap-1">
            {[["specific", "Specific assets"], ["asset_type", "By type"], ["asset_group", "By category"], ["whole_company", "Whole company"], ["mixed", "Mixed"]].map(([v, l]) => (
              <button key={v} data-testid={`cov-method-${v}`} onClick={() => setMethod(v)} className={`text-xs font-semibold px-2 py-1 rounded ${method === v ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
            ))}
          </div>

          <div className="bg-slate-50 rounded p-2 flex flex-col gap-2">
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
              <input type="checkbox" data-testid="cov-exclude-mode" checked={excludeMode} onChange={(e) => setExcludeMode(e.target.checked)} />
              Adding as EXCLUSION (remove from cover)
            </label>
            <div className="flex flex-wrap gap-1">
              <button data-testid="cov-add-whole" onClick={() => addItem("whole_company", null, "include")} className="text-xs bg-blue-100 text-blue-700 font-semibold px-2 py-1 rounded">+ Whole company</button>
              {GROUPS.map(([v, l]) => <button key={v} data-testid={`cov-add-group-${v}`} onClick={() => addItem("asset_group", v, excludeMode ? "exclude" : "include")} className="text-xs bg-slate-100 text-slate-700 font-semibold px-2 py-1 rounded">+ {l}</button>)}
            </div>
            <select data-testid="cov-add-type" className="h-9 border border-slate-300 rounded px-2 text-xs" value="" onChange={(e) => e.target.value && addItem("asset_type", e.target.value, excludeMode ? "exclude" : "include")}>
              <option value="">+ Add asset type…</option>{types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <Input data-testid="cov-asset-search" placeholder="Search assets to add…" value={assetQ} onChange={(e) => setAssetQ(e.target.value)} className="h-9 text-sm" />
            <div className="max-h-36 overflow-y-auto flex flex-col gap-0.5">
              {filteredAssets.map((a) => (
                <button key={a.id} data-testid={`cov-asset-${a.id}`} onClick={() => addItem("asset", a.id, excludeMode ? "exclude" : "include")} className="text-left text-xs px-2 py-1 rounded hover:bg-slate-200">
                  {a.asset_number} — {a.name}{a.registration ? ` (${a.registration})` : ""}
                </button>
              ))}
            </div>
            <div className="flex gap-1">
              <select data-testid="cov-other-scope" id="cov-other-scope" className="h-9 border border-slate-300 rounded px-2 text-xs flex-1">
                <option value="site">Site</option><option value="project">Project</option><option value="role">Role</option><option value="employee">Employee</option>
              </select>
              <input data-testid="cov-other-ref" id="cov-other-ref" placeholder="reference / name" className="h-9 border border-slate-300 rounded px-2 text-xs flex-1" />
              <button data-testid="cov-add-other" onClick={() => { const s = document.getElementById("cov-other-scope").value; const r = document.getElementById("cov-other-ref").value; if (r) { addItem(s, r, "include"); document.getElementById("cov-other-ref").value = ""; } }} className="text-xs bg-slate-700 text-white font-semibold px-2 rounded">Add</button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-2 flex flex-col gap-1" data-testid="coverage-items">
        {items.map((it, idx) => (
          <div key={idx} data-testid={`coverage-item-${idx}`} className={`flex items-center justify-between text-sm rounded px-2 py-1 ${it.mode === "exclude" ? "bg-red-50 text-red-800" : "bg-emerald-50 text-emerald-900"}`}>
            <span>{it.mode === "exclude" ? "✕ Exclude: " : "✓ "}{labelFor(it)}</span>
            {canManage && <button data-testid={`coverage-remove-${idx}`} onClick={() => remove(idx)} className="text-slate-500"><Trash size={14} /></button>}
          </div>
        ))}
        {items.length === 0 && canManage && <p className="text-xs text-slate-400">No coverage lines yet.</p>}
      </div>

      {canManage && (
        <div className="flex gap-2 mt-2">
          <Button data-testid="coverage-preview-btn" onClick={() => runPreview()} variant="outline" className="h-9 font-semibold text-sm">Preview assets</Button>
          <Button data-testid="coverage-save" onClick={saveCoverage} className="h-9 bg-blue-600 hover:bg-blue-700 font-semibold text-sm">Save coverage</Button>
        </div>
      )}
      {preview && (
        <div data-testid="coverage-preview" className="mt-2 bg-white border border-slate-200 rounded p-2 text-sm">
          <div className="font-semibold text-slate-700">{preview.count} asset(s) covered</div>
          <div className="text-xs text-slate-500 mt-1">{preview.assets.slice(0, 20).map((a) => `${a.asset_number}`).join(", ")}{preview.assets.length > 20 ? "…" : ""}</div>
        </div>
      )}
    </div>
  );
}

function DocumentsSection({ policy, canManage, onChanged }) {
  const [cat, setCat] = useState("certificate");
  const [term, setTerm] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("category", "insurance"); fd.append("entity_type", "insurance_policy");
      const up = await api.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/insurance/policies/${policy.id}/documents`, { doc_category: cat, title: file.name, file_id: up.data.id, term_label: term });
      toast.success("Document uploaded"); onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const openDoc = async (fid) => { try { const r = await api.get(`/files/${fid}/url`); window.open(r.data.url, "_blank"); } catch (e) { toast.error("Could not open"); } };
  const archive = async (did) => { try { await api.post(`/insurance/documents/${did}/archive`); toast.success("Archived"); onChanged(); } catch (e) { toast.error("Failed"); } };

  return (
    <div className="mt-5 border-t border-slate-200 pt-4" data-testid="documents-section">
      <div className="font-display font-black text-slate-800 mb-2">Documents</div>
      <div className="flex flex-col gap-1">
        {(policy.documents || []).map((d) => (
          <div key={d.id} data-testid={`policy-doc-${d.id}`} className="flex items-center justify-between text-sm bg-white border border-slate-200 rounded px-2 py-1.5">
            <button onClick={() => openDoc(d.file_id)} className="flex items-center gap-1 text-blue-700 font-semibold text-left"><FileText size={15} weight="bold" />{(DOC_CATEGORIES.find((c) => c[0] === d.doc_category) || [])[1] || d.doc_category}{d.term_label ? ` · ${d.term_label}` : ""}<span className="text-slate-400 font-normal">· {d.title}</span></button>
            {canManage && <button data-testid={`policy-doc-archive-${d.id}`} onClick={() => archive(d.id)} className="text-slate-400"><Trash size={14} /></button>}
          </div>
        ))}
        {(policy.documents || []).length === 0 && <p className="text-xs text-slate-400">No documents uploaded.</p>}
      </div>
      {canManage && (
        <div className="flex flex-wrap items-center gap-2 mt-2 bg-slate-50 rounded p-2">
          <select data-testid="doc-category" value={cat} onChange={(e) => setCat(e.target.value)} className="h-9 border border-slate-300 rounded px-2 text-xs">
            {DOC_CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <Input data-testid="doc-term" placeholder="Term e.g. 2026/27" value={term} onChange={(e) => setTerm(e.target.value)} className="h-9 text-xs w-32" />
          <input ref={fileRef} data-testid="doc-file" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif" onChange={upload} disabled={busy} className="text-xs" />
          {busy && <span className="text-xs text-slate-500">Uploading…</span>}
        </div>
      )}
    </div>
  );
}

function LifecycleSection({ policy, onChanged, onClose }) {
  const [replacing, setReplacing] = useState(false);
  const [rf, setRf] = useState({ name: "", policy_number: "", effective_from: "", effective_until: "" });
  const cancel = async () => {
    const reason = window.prompt("Reason for cancelling this policy?");
    if (reason === null) return;
    try { await api.post(`/insurance/policies/${policy.id}/cancel`, { reason }); toast.success("Policy cancelled"); onChanged(); }
    catch (e) { toast.error("Failed"); }
  };
  const doReplace = async () => {
    try {
      await api.post(`/insurance/policies/${policy.id}/replace`, { ...rf, policy_type_id: policy.policy_type_id, provider_id: policy.provider_id, broker: policy.broker, effective_from: rf.effective_from || null, effective_until: rf.effective_until || null, copy_coverage: true });
      toast.success("Replacement policy created (coverage copied)"); onChanged(); onClose();
    } catch (e) { toast.error("Failed"); }
  };
  return (
    <div className="mt-5 border-t border-slate-200 pt-4" data-testid="lifecycle-section">
      <div className="font-display font-black text-slate-800 mb-2">Policy lifecycle</div>
      <div className="flex flex-wrap gap-2">
        {policy.lifecycle !== "cancelled" && <Button data-testid="policy-cancel" onClick={cancel} variant="outline" className="h-9 text-red-700 border-red-300 font-semibold text-sm">Cancel policy</Button>}
        {!policy.replaced_by_policy && <Button data-testid="policy-replace-open" onClick={() => setReplacing((v) => !v)} className="h-9 bg-slate-800 hover:bg-slate-700 font-semibold text-sm"><ArrowClockwise size={14} weight="bold" className="mr-1" />Create replacement</Button>}
      </div>
      {replacing && (
        <div className="mt-2 bg-slate-50 rounded p-2 flex flex-col gap-2" data-testid="replace-form">
          <p className="text-xs text-slate-500">Creates a new policy that replaces this one; coverage is copied forward and this policy is marked "replaced".</p>
          <Input data-testid="replace-name" placeholder="New policy name" value={rf.name} onChange={(e) => setRf({ ...rf, name: e.target.value })} className="h-9 text-sm" />
          <Input data-testid="replace-number" placeholder="New policy number" value={rf.policy_number} onChange={(e) => setRf({ ...rf, policy_number: e.target.value })} className="h-9 text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="replace-start" type="date" value={rf.effective_from} onChange={(e) => setRf({ ...rf, effective_from: e.target.value })} className="h-9 text-sm" />
            <Input data-testid="replace-expiry" type="date" value={rf.effective_until} onChange={(e) => setRf({ ...rf, effective_until: e.target.value })} className="h-9 text-sm" />
          </div>
          <Button data-testid="replace-confirm" onClick={doReplace} className="h-9 bg-blue-600 hover:bg-blue-700 font-semibold text-sm">Create replacement</Button>
        </div>
      )}
    </div>
  );
}

function AssetTypeRequirements({ isAdmin }) {
  const [types, setTypes] = useState([]);
  const [ptypes, setPtypes] = useState([]);
  const load = () => api.get("/config/asset-types").then((r) => setTypes(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/config/insurance-policy-types").then((r) => setPtypes(r.data)).catch(() => {}); }, []);
  const save = async (t, patch) => { try { await api.put(`/config/asset-types/${t.id}`, patch); toast.success("Requirement saved"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  return (
    <div className="flex flex-col gap-2" data-testid="asset-type-requirements">
      <p className="text-sm text-slate-500">Set which asset types require insurance and the policy type that satisfies it. Individual assets can override this on their Insurance tab. {!isAdmin && "(Admin only to edit.)"}</p>
      {types.map((t) => (
        <div key={t.id} data-testid={`req-type-${t.id}`} className="bg-white border-2 border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-3">
          <div className="font-display font-black text-slate-900 flex-1 min-w-[140px]">{t.name}{t.road_vehicle ? <span className="ml-2 text-[10px] uppercase bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">road</span> : null}</div>
          <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" data-testid={`req-toggle-${t.id}`} disabled={!isAdmin} checked={!!t.insurance_required} onChange={(e) => save(t, { insurance_required: e.target.checked })} />Insurance required</label>
          <select data-testid={`req-ptype-${t.id}`} disabled={!isAdmin || !t.insurance_required} value={t.insurance_policy_type_id || ""} onChange={(e) => save(t, { insurance_policy_type_id: e.target.value || null })} className="h-9 border-2 border-slate-300 rounded px-2 text-sm disabled:opacity-50">
            <option value="">Any policy type</option>
            {ptypes.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      ))}
    </div>
  );
}
